import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { PageHeader } from "@/components/common/PageHeader";
import { StatusBadge } from "@/components/common/StatusBadge";
import { EmptyState } from "@/components/common/EmptyState";
import { query } from "@/services/services";
import { formatDate, daysUntil } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PendingCreations } from "@/components/matter/CreationRecovery";
import { NewMatterDialog } from "@/components/shell/NewMatterDialog";
import { AIIntakeDialog } from "@/components/shell/AIIntakeDialog";
import { QuickAddDialogs } from "@/components/shell/QuickAddDialogs";
import { LimitationBadge } from "@/components/common/LimitationBadge";
import { cn } from "@/lib/utils";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Search, MoreHorizontal, ArrowUpDown, Briefcase, SlidersHorizontal, Sparkles } from "lucide-react";

const QUICK_FILTERS = ["All", "My Matters", "Active", "Urgent", "Hearing Soon", "Needs Attention", "Drafting", "Awaiting Client", "Closed", "Archived"];
const KANBAN_STAGES = ["Filing", "Admission", "Notice Issued", "Counter Filed", "Pleadings", "Evidence", "Arguments", "Interim Stay", "Final Hearing", "Drafting"];

export default function Matters() {
  const { db, can, clientScopeId, scope } = useApp();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    if (can("matter.create")) {
      if (params.get("new") === "1") setShowNew(true);
      if (params.get("ai") === "1") setShowAI(true);
    }
    if (params.get("new") || params.get("ai")) setParams({}, { replace: true });
    // eslint-disable-next-line
  }, []);
  const scopedMatters = useMemo(
    () => (clientScopeId ? db.matters.filter((m) => m.clientId === clientScopeId) : db.matters),
    [db.matters, clientScopeId]
  );
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("All");
  const [court, setCourt] = useState("all");
  const [area, setArea] = useState("all");
  const [showAI, setShowAI] = useState(false);
  const [rep, setRep] = useState("all");
  const [lead, setLead] = useState("all");
  const [view, setView] = useState(() => localStorage.getItem("nyk_matters_view") || "table");
  const setViewPersist = (v) => { setView(v); localStorage.setItem("nyk_matters_view", v); };
  const [sort, setSort] = useState({ key: "createdAt", dir: "desc" });
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [showNew, setShowNew] = useState(false);
  const [quick, setQuick] = useState(null);

  const courts = useMemo(() => [...new Set(scopedMatters.map((m) => m.court).filter(Boolean))], [scopedMatters]);
  const areas = useMemo(() => [...new Set(scopedMatters.map((m) => m.practiceArea).filter(Boolean))], [scopedMatters]);
  const reps = useMemo(() => [...new Set(scopedMatters.map((m) => m.representationMode).filter(Boolean))], [scopedMatters]);
  const leads = useMemo(() => [...new Set(scopedMatters.map((m) => m.leadId).filter(Boolean))], [scopedMatters]);
  const needsAttention = (m) => { const d = daysUntil(m.nextHearing), l = daysUntil(m.limitationDate); return (d != null && d >= 0 && d <= 2) || (l != null && l <= 7) || m.legalHold?.active || db.tasks.some((t) => t.matterId === m.id && t.status !== "Completed" && t.dueDate && new Date(t.dueDate) < new Date()); };

  const filtered = useMemo(() => {
    let rows = scopedMatters.filter((m) => {
      const q = search.toLowerCase();
      const matchSearch = !q || m.title.toLowerCase().includes(q) || m.caseNumber.toLowerCase().includes(q) || query.clientName(db, m.clientId).toLowerCase().includes(q) || (m.representedEntity || "").toLowerCase().includes(q) || (m.efilingNumber || "").toLowerCase().includes(q) || (m.tags || []).some((t) => t.toLowerCase().includes(q)) || (m.court || "").toLowerCase().includes(q);
      const matchCourt = court === "all" || m.court === court;
      const matchArea = area === "all" || m.practiceArea === area;
      const matchRep = rep === "all" || m.representationMode === rep;
      const matchLead = lead === "all" || m.leadId === lead;
      let matchFilter = true;
      if (filter === "Active") matchFilter = m.status === "Active";
      else if (filter === "My Matters") matchFilter = m.leadId === scope.dirId || (m.assignedTo || []).includes(scope.dirId);
      else if (filter === "Needs Attention") matchFilter = needsAttention(m);
      else if (filter === "Urgent") matchFilter = m.priority === "High";
      else if (filter === "Hearing Soon") { const d = daysUntil(m.nextHearing); matchFilter = d != null && d >= 0 && d <= 7; }
      else if (filter === "Drafting") matchFilter = m.status === "Draft" || m.stage === "Drafting";
      else if (filter === "Awaiting Client") matchFilter = m.status === "Awaiting Client";
      else if (filter === "Closed") matchFilter = ["Disposed", "Closed"].includes(m.status);
      else if (filter === "Archived") matchFilter = m.status === "Archived";
      return matchSearch && matchCourt && matchArea && matchRep && matchLead && matchFilter;
    });
    rows = [...rows].sort((a, b) => {
      let av = a[sort.key], bv = b[sort.key];
      if (sort.key === "client") { av = query.clientName(db, a.clientId); bv = query.clientName(db, b.clientId); }
      if (av == null) return 1; if (bv == null) return -1;
      return sort.dir === "asc" ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1);
    });
    return rows;
  }, [db, search, filter, court, area, rep, lead, sort, scopedMatters]); // eslint-disable-line

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const pageRows = filtered.slice((page - 1) * perPage, page * perPage);
  const activeFilterCount = (court !== "all" ? 1 : 0) + (area !== "all" ? 1 : 0) + (rep !== "all" ? 1 : 0) + (lead !== "all" ? 1 : 0) + (filter !== "All" ? 1 : 0);

  const toggleSort = (key) => setSort((s) => ({ key, dir: s.key === key && s.dir === "asc" ? "desc" : "asc" }));
  const clearAll = () => { setFilter("All"); setCourt("all"); setArea("all"); setRep("all"); setLead("all"); setSearch(""); };

  return (
    <div>
      <PageHeader title="My Matters" subtitle={scope.fullAccess ? "Manage litigation, advisory and transactional work." : `Showing only matters assigned to you (${scope.role}).`}>
        {can("matter.create") && <Button variant="outline" onClick={() => setShowAI(true)} data-testid="ai-intake-btn"><Sparkles className="mr-1 h-4 w-4 text-copper" /> AI Intake</Button>}
        {can("matter.create") && <Button onClick={() => setShowNew(true)} data-testid="new-matter-btn">+ New Matter</Button>}
      </PageHeader>
      <PendingCreations />

      {/* Quick filters */}
      <div className="mb-3 flex flex-wrap items-center gap-1.5">
        {QUICK_FILTERS.map((f) => (
          <button key={f} onClick={() => { setFilter(f); setPage(1); }} data-testid={`filter-${f.toLowerCase().replace(/\s/g, "-")}`}
            className={cn("rounded-full px-3 py-1 text-xs font-medium transition-colors", filter === f ? "bg-navy text-white" : "border border-line bg-card text-ink-2 hover:border-copper/40")}>
            {f}
          </button>
        ))}
      </div>

      {/* Toolbar */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="relative min-w-[240px] flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted" />
          <Input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} placeholder="Search matters, case numbers, clients…" className="pl-9" data-testid="matters-search" />
        </div>
        <Select value={court} onValueChange={(v) => { setCourt(v); setPage(1); }}>
          <SelectTrigger className="w-[200px]" data-testid="filter-court"><SelectValue placeholder="Court" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Courts</SelectItem>{courts.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={area} onValueChange={(v) => { setArea(v); setPage(1); }}>
          <SelectTrigger className="w-[190px]" data-testid="filter-area"><SelectValue placeholder="Practice Area" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Practice Areas</SelectItem>{areas.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
        </Select>
        {reps.length > 0 && <Select value={rep} onValueChange={(v) => { setRep(v); setPage(1); }}>
          <SelectTrigger className="w-[190px]" data-testid="filter-representation"><SelectValue placeholder="Representation" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Representation</SelectItem>{reps.map((x) => <SelectItem key={x} value={x}>{x.replaceAll("_", " ")}</SelectItem>)}</SelectContent>
        </Select>}
        <Select value={lead} onValueChange={(v) => { setLead(v); setPage(1); }}>
          <SelectTrigger className="w-[170px]" data-testid="filter-lead"><SelectValue placeholder="Lead Counsel" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Leads</SelectItem>{leads.map((x) => <SelectItem key={x} value={x}>{query.userName(db, x)}</SelectItem>)}</SelectContent>
        </Select>
        <div className="flex rounded-md border border-line bg-card p-0.5 text-xs" data-testid="view-toggle">
          {[["table", "Table"], ["compact", "Compact"], ["kanban", "Kanban"]].map(([k, l]) => <button key={k} onClick={() => setViewPersist(k)} data-testid={`view-${k}`} className={cn("rounded px-2.5 py-1", view === k ? "bg-navy text-white" : "text-ink-2")}>{l}</button>)}
        </div>
        {activeFilterCount > 0 && (
          <Button variant="ghost" size="sm" onClick={clearAll} data-testid="clear-filters">
            <SlidersHorizontal className="mr-1 h-3.5 w-3.5" /> Clear ({activeFilterCount})
          </Button>
        )}
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <EmptyState icon={Briefcase} title="No matters found" message="No matters match your filters. Try adjusting or clearing them." actionLabel={can("matter.create") ? "Create Your First Matter" : undefined} onAction={() => setShowNew(true)} />
      ) : view === "kanban" ? (
        <div className="flex gap-3 overflow-x-auto pb-3" data-testid="matters-kanban">
          {[...KANBAN_STAGES, ...[...new Set(filtered.map((m) => m.stage))].filter((s) => !KANBAN_STAGES.includes(s))].map((stage, si) => {
            const col = filtered.filter((m) => m.stage === stage);
            if (!col.length) return null;
            return (
              <div key={`${stage}-${si}`} className="w-[260px] shrink-0 rounded-xl border border-line bg-surface p-2" data-testid={`kanban-col-${stage.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <div className="mb-2 flex items-center justify-between px-1 text-xs font-semibold uppercase tracking-wide text-ink-muted">{stage}<span>{col.length}</span></div>
                <div className="space-y-2">{col.map((m) => (
                  <button key={m.id} onClick={() => navigate(`/matters/${m.id}`)} className="w-full rounded-lg border border-line bg-card p-2.5 text-left hover:border-copper/40" data-testid={`kanban-card-${m.id}`}>
                    <div className="truncate text-sm font-medium text-ink">{m.title}</div>
                    <div className="truncate text-xs text-ink-2">{m.caseNumber} · {query.clientName(db, m.clientId)}</div>
                    <div className="mt-1.5 flex items-center justify-between text-[11px] text-ink-muted"><span>{m.nextHearing ? `Hearing ${formatDate(m.nextHearing, "dd MMM")}` : "No hearing"}</span>{needsAttention(m) && <span className="rounded bg-danger/10 px-1.5 py-0.5 font-medium text-danger">attention</span>}</div>
                    {m.limitationDate && <div className="mt-1.5"><LimitationBadge limitationDate={m.limitationDate} size="xs" /></div>}
                  </button>
                ))}</div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-line bg-card">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-line bg-surface text-left text-xs uppercase tracking-wide text-ink-muted">
                  <Th onClick={() => toggleSort("title")}>Matter</Th>
                  <th className="px-3 py-2.5 font-medium">Case No.</th>
                  <Th onClick={() => toggleSort("client")}>Client</Th>
                  <th className="px-3 py-2.5 font-medium">Court</th>
                  <th className="px-3 py-2.5 font-medium">Stage</th>
                  <Th onClick={() => toggleSort("nextHearing")}>Next Hearing</Th>
                  <th className="px-3 py-2.5 font-medium">Limitation</th>
                  <Th onClick={() => toggleSort("status")}>Status</Th>
                  <th className="px-3 py-2.5 font-medium">Priority</th>
                  <th className="px-3 py-2.5" />
                </tr>
              </thead>
              <tbody>
                {pageRows.map((m) => {
                  const lim = daysUntil(m.limitationDate);
                  return (
                    <tr key={m.id} className={cn("cursor-pointer border-b border-line last:border-0 hover:bg-surface", view === "compact" && "[&_td]:py-1 [&_td]:text-xs")} onClick={() => navigate(`/matters/${m.id}`)} data-testid={`matter-row-${m.id}`}>
                      <td className="px-3 py-2.5 font-medium text-ink max-w-[240px]"><div className="truncate">{m.title}</div><div className="text-xs font-normal text-ink-muted">{m.practiceArea}</div></td>
                      <td className="px-3 py-2.5 text-ink-2 whitespace-nowrap">{m.caseNumber}</td>
                      <td className="px-3 py-2.5 text-ink-2">{query.clientName(db, m.clientId)}</td>
                      <td className="px-3 py-2.5 text-ink-2 max-w-[160px]"><div className="truncate">{m.court}</div></td>
                      <td className="px-3 py-2.5 text-ink-2">{m.stage}</td>
                      <td className="px-3 py-2.5 text-ink-2 whitespace-nowrap">{formatDate(m.nextHearing)}</td>
                      <td className="px-3 py-2.5 whitespace-nowrap">{m.limitationDate ? <LimitationBadge limitationDate={m.limitationDate} onClick={() => navigate(`/matters/${m.id}`)} /> : "—"}</td>
                      <td className="px-3 py-2.5"><StatusBadge status={m.status} /></td>
                      <td className="px-3 py-2.5"><StatusBadge status={m.priority} /></td>
                      <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button className="rounded p-1 hover:bg-muted" data-testid={`matter-menu-${m.id}`}><MoreHorizontal className="h-4 w-4 text-ink-2" /></button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => navigate(`/matters/${m.id}`)}>Open</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setQuick({ type: "hearing", matterId: m.id })}>Add Hearing</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setQuick({ type: "task", matterId: m.id })}>Add Task</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setQuick({ type: "document", matterId: m.id })}>Upload Document</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setQuick({ type: "billing", matterId: m.id })}>Add Billing Entry</DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem disabled={!can("matter.close")} data-testid={`matter-close-${m.id}`} onClick={() => navigate(`/matters/${m.id}?action=close`)}>Review & Close Matter</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {/* Pagination */}
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line px-4 py-2.5 text-xs text-ink-2">
            <div className="flex items-center gap-2">
              <span>Rows per page</span>
              <Select value={String(perPage)} onValueChange={(v) => { setPerPage(Number(v)); setPage(1); }}>
                <SelectTrigger className="h-7 w-[70px]"><SelectValue /></SelectTrigger>
                <SelectContent>{[10, 25, 50].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <span>{(page - 1) * perPage + 1}–{Math.min(page * perPage, filtered.length)} of {filtered.length}</span>
              <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>Prev</Button>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</Button>
            </div>
          </div>
        </div>
      )}

      <NewMatterDialog open={showNew} onOpenChange={setShowNew} />
      <AIIntakeDialog open={showAI} onOpenChange={setShowAI} />
      {quick && <QuickAddDialogs type={quick.type} context={{ matterId: quick.matterId }} onClose={() => setQuick(null)} />}
    </div>
  );
}

function Th({ children, onClick }) {
  return (
    <th className="cursor-pointer select-none px-3 py-2.5 font-medium hover:text-ink" onClick={onClick}>
      <span className="inline-flex items-center gap-1">{children}<ArrowUpDown className="h-3 w-3 opacity-50" /></span>
    </th>
  );
}
